import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';

/** 解析表单 inferenceFrameworkImage 字段为镜像列表项 */
export function parseInferenceFrameworkImageItem(raw: unknown): CommonImageItemType | null {
  const parsed = parseFormJsonField<CommonImageItemType>(raw);
  if (!parsed) return null;
  const key = String(parsed.id ?? parsed.key ?? '').trim();
  const name = String(parsed.name ?? '').trim();
  if (!key && !name) return null;
  return { ...parsed, key: key || name };
}

/** 从 _inferenceSystem 构造最小展示用镜像项（无 inferenceFrameworkImage 时兜底） */
export function buildImageItemFromInferenceSystem(system: Record<string, unknown>): CommonImageItemType | null {
  const imageKey = String(system.imageKey ?? '').trim();
  const imageName = String(system.imageName ?? '').trim();
  const imageUrl = String(system.image || system.url || '').trim();
  if (!imageKey && !imageName && !imageUrl) return null;

  const architecture = String(system.architecture ?? '').trim();
  const minDiskGi = system.minSystemDiskGi;
  const options = (system.options as Record<string, unknown> | undefined) || undefined;
  const containerDeployConfig = options?.containerDeployConfig;

  return {
    id: imageKey || imageName || imageUrl,
    key: imageKey || imageName || imageUrl,
    name: imageName || imageUrl,
    description: '',
    abstractId: '',
    workspace: '',
    artifactType: '',
    vendor: String(system.os ?? ''),
    version: String(system.version ?? ''),
    kernelVersion: '',
    cloudinitVersion: '',
    tag: [],
    visibility: '',
    tenantAclWorkspaces: [],
    status: '',
    formation: '',
    cpuCompatibility: '',
    cpuArchitectures: architecture ? [architecture] : [],
    hardwareCompatibility: '',
    hardwareConfig: [],
    httpSourceUrl: '',
    httpSourceMd5: '',
    httpSourceSize: '',
    httpRefUrl: '',
    dockerSourceUrl: '',
    dockerSourceMd5: '',
    dockerSourceSize: '',
    dockerRefUrl: imageUrl,
    gitSourceUrl: '',
    gitSourceMd5: '',
    gitSourceSize: '',
    gitRefUrl: '',
    options: {
      systemImage: {
        systemVolumeSize: minDiskGi != null ? `${minDiskGi}Gi` : undefined,
        bootloader: system.bootloader,
      },
    },
    ...(containerDeployConfig != null
      ? { containerDeployConfig: typeof containerDeployConfig === 'string' ? containerDeployConfig : JSON.stringify(containerDeployConfig) }
      : {}),
    creator: '',
    installScript: '',
    fileName: '',
    createTime: '',
    updateTime: '',
  };
}

/** presetConfig 是否已配置内置/自定义推理镜像（下单场景用于锁定镜像选择器） */
export function hasPresetInferenceImage(presetConfig: Record<string, unknown>): boolean {
  return Boolean(resolvePresetInferenceImageItem(presetConfig));
}

/** 从 presetConfig 或表单值解析只读展示的镜像项 */
export function resolvePresetInferenceImageItem(values: Record<string, unknown>): CommonImageItemType | null {
  const fromCatalog = parseInferenceFrameworkImageItem(values.inferenceFrameworkImage);
  if (fromCatalog) return fromCatalog;

  const system = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  if (!system) return null;
  return buildImageItemFromInferenceSystem(system);
}
